(() => {
  if (window.apmInlineInstalled) return;
  window.apmInlineInstalled = true;
  const config = document.currentScript?.dataset || {};
  const families = {
    'apple-iphone-18-pro': ['iphone-pro', 'apmPro'],
    'apple-iphone-pro': ['iphone-pro', 'apmPro'],
    'apple-iphone-duo': ['iphone-duo', 'apmDuo'],
    'apple-airpods-5': ['airpods-5', 'apmAirpods'],
    'apple-ipad-pro-m5-11': ['ipad-pro', 'apmIpad'],
    'apple-ipad-pro-m5-13': ['ipad-pro', 'apmIpad'],
    'apple-ipad-air-m4-11': ['ipad-air', 'apmIpadAir'],
    'apple-ipad-air-m4-13': ['ipad-air', 'apmIpadAir']
  };
  const pending = new Map();
  function loadRuntime(family, src) {
    if (window.apmInlineRenderers?.[family]) return Promise.resolve();
    if (!pending.has(family)) pending.set(family, new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = () => { pending.delete(family); reject(Error('Overview script could not load')); };
      document.head.append(script);
    }));
    return pending.get(family);
  }
  function context(root, body) {
    const events = new EventTarget();
    const local = {};
    const isolated = type => type === 'pageshow' || type.startsWith('channel:');
    const doc = new Proxy(document, {get(target, key) {
      if (key === 'querySelector' || key === 'querySelectorAll' || key === 'getElementById') return root[key].bind(root);
      if (key === 'addEventListener' || key === 'removeEventListener' || key === 'dispatchEvent') return root[key].bind(root);
      if (key === 'body' || key === 'documentElement') return body;
      if (key === 'activeElement') return root.activeElement;
      if (key === 'readyState') return 'complete';
      const value = target[key];
      return typeof value === 'function' ? value.bind(target) : value;
    }});
    const win = new Proxy(window, {get(target, key) {
      if (key in local) return local[key];
      if (key === 'document') return doc;
      if (key === 'addEventListener' || key === 'removeEventListener') return (type, fn, options) => (isolated(type) ? events : target)[key](type, fn, options);
      if (key === 'dispatchEvent') return event => (isolated(event.type) ? events : target).dispatchEvent(event);
      const value = target[key];
      if (['getComputedStyle','matchMedia','requestAnimationFrame','cancelAnimationFrame'].includes(key)) return value.bind(target);
      return value;
    }, set(target, key, value) { local[key] = value; return true; }});
    return [win, doc];
  }
  async function mount(node, url, family, key) {
    const host = document.createElement('div');
    host.className = 'apm-inline-overview';
    host.style.cssText = 'display:block;width:100%;clear:both';
    node.replaceWith(host);
    const legacy = host.closest('.description-wrapper');
    if (legacy) {
      legacy.style.setProperty('max-height', 'none', 'important');
      legacy.style.setProperty('height', 'auto', 'important');
      legacy.classList.remove('collapsed');
      legacy.querySelector('.description-fade-overlay')?.remove();
      if (legacy.nextElementSibling?.matches('.toggle-description-button')) legacy.nextElementSibling.remove();
    }
    const shadow = host.attachShadow({mode:'open'});
    const style = document.createElement('style');
    style.textContent = `:host{display:block;text-align:left;font-family:Arial,sans-serif;color:#1d1d1f;font-size:16px;line-height:normal;letter-spacing:normal} .clip{position:relative;max-height:600px;overflow:hidden} .clip[data-open]{max-height:none;overflow:visible} .clip:not([data-open])::after{content:"";position:absolute;inset:0;z-index:10;background:linear-gradient(transparent 70%,#fff);pointer-events:auto} .toggle{display:block;margin:20px auto;padding:14px 26px;border:0;border-radius:28px;background:#1d1d1f;color:white;font:600 16px Arial;cursor:pointer}.toggle:focus-visible{outline:3px solid #0071e3;outline-offset:4px} .status{padding:24px;text-align:center}.status a{color:#0071e3} @media(max-width:600px){.clip{max-height:420px}}`;
    style.textContent += `
      .channel-html .icon-chevrondown::before,
      .channel-html .icon-chevronright::before { display:none!important; }
      .channel-html .icon-chevrondown::after,
      .channel-html .icon-chevronright::after {
        content:""!important; display:inline-block!important;
        box-sizing:border-box!important; width:0.55em!important; height:0.55em!important;
        border:0!important; border-right:2px solid currentColor!important;
        border-bottom:2px solid currentColor!important;
        padding:0!important; margin:0 0.15em!important;
        background:none!important; position:relative!important;
        top:-0.15em!important; transform:rotate(45deg)!important;
        pointer-events:none!important;
      }
      .channel-html .icon-chevronright::after {
        transform:rotate(-45deg)!important; top:0!important;
      }
    `;
    const status = document.createElement('p');
    status.className = 'status';
    status.textContent = 'Loading overview…';
    status.setAttribute('role','status');
    shadow.append(style, status);
    try {
      if (!config[key] || !config[key+'Css']) throw Error('Missing overview asset configuration');
      const response = await fetch(url.href, {credentials:'same-origin'});
      if (!response.ok || new URL(response.url).origin !== location.origin) throw Error('Overview page unavailable');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = parsed.querySelector('[data-apm-content]');
      const images = parsed.querySelector('[data-apm-images]');
      const ctas = parsed.querySelector('[data-apm-ctas]');
      if (!source || !images) throw Error('Assign the Apple page template to the overview page');
      const css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = config[key+'Css'];
      const readyCSS = new Promise((resolve,reject) => {css.onload=resolve;css.onerror=()=>reject(Error('Overview stylesheet could not load'));});
      shadow.append(css);
      await Promise.all([readyCSS,loadRuntime(family, config[key])]);
      const clip = document.createElement('div');
      clip.className = 'clip'; clip.id = 'overview-content';
      const body = document.createElement('div');
      body.append(document.importNode(images,true));
      if (ctas) body.append(document.importNode(ctas,true));
      const content = document.importNode(source,true);
      content.querySelectorAll('script').forEach(script=>script.remove());
      body.append(content); clip.append(body); shadow.append(clip);
      const [win,doc] = context(shadow,body);
      window.apmInlineRenderers[family](win,doc);
      body.inert = true;
      const button = document.createElement('button');
      button.className = 'toggle'; button.type = 'button';
      button.textContent = 'View full overview';
      button.setAttribute('aria-expanded','false'); button.setAttribute('aria-controls',clip.id);
      button.addEventListener('click', () => {
        const open = !clip.hasAttribute('data-open');
        clip.toggleAttribute('data-open',open); body.inert = !open;
        button.textContent = open ? 'Show less' : 'View full overview';
        button.setAttribute('aria-expanded',String(open));
        win.dispatchEvent(new CustomEvent('channel:user-refresh'));
        if (!open) {host.scrollIntoView({block:'start'});window.scrollBy(0,-180);}
      });
      shadow.addEventListener('click',event=>{
        const anchor = event.target.closest?.('a[href^="#"]');
        if (!anchor) return;
        const id = anchor.getAttribute('href').slice(1);
        const target = shadow.getElementById(id);
        if (target) {event.preventDefault(); target.scrollIntoView({block:'center'});}
      });
      shadow.append(button); status.remove();
    } catch(error) {
      shadow.querySelector('.clip')?.remove();
      status.textContent = 'The overview could not load here. ';
      const link = document.createElement('a'); link.href=url.href; link.textContent='View full overview';
      status.append(link);
      console.warn('Apple overview:',error.message);
    }
  }
  function discover() {
    document.querySelectorAll('iframe[src], .apm-product-overview[data-page]').forEach(node=>{
      try {
        const url = new URL(node.getAttribute('data-page') || node.getAttribute('src'),location.href);
        const match = url.pathname.match(/\/pages\/([^/]+)\/?$/);
        const family = match && families[match[1]];
        if (url.origin === location.origin && family) mount(node,url,...family);
      } catch { /* Ignore unrelated embeds. */ }
    });
  }
  let queued = false;
  new MutationObserver(()=>{if (!queued) {queued=true;requestAnimationFrame(()=>{queued=false;discover();});}}).observe(document.documentElement,{childList:true,subtree:true});
  discover();
})();
